import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import { IRichTextInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import { IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";
import { ArrowCircleIcon } from "../icons/ArrowCircleIcon";
import { SmallSquareIcon } from "../icons/SmallSquareIcon";

export interface CollectionContent {
  content: JSX.Element;
  background?: Queries.GraphCMS_Colour | null;
  contentBackgroundClassName?: string;
  accentColourClassName?: string;
  notSelectedAccentColourClassName?: string;
  textColourClassName?: string;
}

export interface CollectionProps {
  title?: string | null;
  showTitle?: boolean | null;
  message?: IRichTextInformation | null;
  forceScroll?: boolean | null;
  imageSizing?: string | null;
  contents: Array<CollectionContent>;
  archiveLink?: JSX.Element | null;
  image?: IGatsbyImageData | null;
  alt?: string | null;
  shape?: IGatsbySvg | null;
  className?: string;
  slideIndex?: number;
  setSlideIndex?: (index: number) => void;
  pageTypeFilter?: Queries.GraphCMS_PageType | null;
  pageFilterCount?: number | null;
  showFilter?: boolean | null;
  showLargeFirstResult?: boolean | null;
  contactForm?: Queries.CollectionFragment["contactForm"] | null;
  locale?: string | null;
}

export const Collection: React.FC<CollectionProps> = ({
  forceScroll,
  contents,
  title,
  className,
  showTitle,
  slideIndex,
  setSlideIndex,
}) => {
  if (contents.length === 0) {
    return <p>No contents</p>;
  }
  const showNav = false;
  const currentContent = contents[slideIndex ?? 0];
  const currentSlide = currentContent.content;

  return (
    <section
      data-component="Collection"
      className={classNames("relative", className)}
    >
      {showTitle && title && (
        <div className="max-width relative">
          <h2
            className={classNames(
              "h2 absolute z-20 hidden lg:left-desktop-1-margin-cols lg:block lg:pt-40px",
              currentContent.textColourClassName ?? "text-cotton",
            )}
          >
            {title}
          </h2>
        </div>
      )}
      {showNav ? currentSlide : contents.map((c) => c.content)}
      {showNav && (
        <div
          className={classNames(
            "absolute",
            "right-0",
            "top-0",
            "h-full",
            "w-full",
            "page-grid",
            "grid-rows-3",
            "lg:grid-rows-1",
            "pointer-events-none",
          )}
        >
          <div
            className={classNames(
              "col-start-6",
              "md:col-start-10",
              "lg:col-start-15",
              "row-start-3",
              "md:row-start-1",
              "md:h-full",
              "col-span-1",
              "flex",
              "flex-col",
              "items-end",
              "justify-center",
              "pointer-events-none",
            )}
          >
            {contents?.map((content, i) => {
              const isCurrentSlide = i === slideIndex;
              return (
                <button
                  key={i}
                  onClick={() => setSlideIndex?.(i)}
                  className={classNames(
                    "flex",
                    "py-10px",
                    "px-10px",
                    "pointer-events-auto",
                  )}
                  aria-label={`Switch slide to slide index ${i}`}
                >
                  <SmallSquareIcon
                    colourClassName={
                      isCurrentSlide
                        ? content.accentColourClassName
                        : content.notSelectedAccentColourClassName
                    }
                  />
                </button>
              );
            })}
          </div>
          {forceScroll && (
            <button
              aria-label="Force scroll"
              className={classNames(
                "col-start-5",
                "lg:col-start-15",
                "row-start-3",
                "lg:row-start-1",
                "col-span-1",
                "flex",
                "flex-col",
                "items-end",
                "justify-end",
                "pb-48px",
              )}
            >
              <ArrowCircleIcon
                className="h-60px"
                colourClassName={
                  contents[slideIndex ?? 0].accentColourClassName
                }
              />
            </button>
          )}
        </div>
      )}
    </section>
  );
};
